import { useEffect, useState } from 'react';
import clsx from 'clsx';

/**
 * Minimal sticky navigation.
 *
 * Transparent over the hero, taking on a background only once the page has
 * scrolled, so the opening photograph is never framed by a bar.
 */
const LINKS = [
  { href: '#work', label: 'How it works' },
  { href: '#privacy', label: 'Privacy' },
  { href: '#price', label: 'Price' },
  // A page, not an anchor, and deliberately not called "About us". Nobody has
  // ever clicked a nav link out of a burning need to read about us.
  { href: '/about', label: 'Who made this' },
];

export default function SiteNav({
  onTry,
  busy,
  shootCount = 0,
  onShoots,
}: {
  onTry: () => void;
  busy?: boolean;
  shootCount?: number;
  onShoots?: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={clsx(
        'sticky top-0 z-40 transition-colors duration-300',
        scrolled
          ? 'border-b border-ink-850 bg-ink-950/85 backdrop-blur-xl'
          : 'border-b border-transparent',
      )}
    >
      <nav className="mx-auto flex h-16 w-full max-w-content items-center gap-6 px-5 sm:px-8">
        <a href="#top" className="flex items-center gap-2.5" aria-label="m3cull, back to top">
          <img
            src={`${import.meta.env.BASE_URL}brand/lockup.png`}
            alt="m3cull"
            width={394}
            height={96}
            className="h-6 w-auto sm:h-7"
          />
        </a>

        <div className="ml-auto hidden items-center gap-7 md:flex">
          {LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-caption text-ink-400 transition-colors hover:text-ink-50"
            >
              {link.label}
            </a>
          ))}
          {/*
            Always here, even at zero. Hiding it until the first import meant a
            first-time visitor had no idea the library existed, and someone who
            had deleted their last shoot watched the way back disappear. The
            count is the part that is conditional: a badge reading 0 is noise.
          */}
          <button
            onClick={onShoots}
            className="flex items-center gap-1.5 text-caption text-ink-200 transition-colors hover:text-ink-50"
          >
            Shoots
            {shootCount > 0 && (
              <span className="tnum rounded-full border border-ink-700 px-1.5 text-[10.5px] text-ink-400">
                {shootCount}
              </span>
            )}
          </button>
        </div>

        <button className="btn-primary ml-auto md:ml-0" onClick={onTry} disabled={busy}>
          Try m3cull
        </button>
      </nav>
    </header>
  );
}
